// ============================================================================
// UsageLimitsPanel — org token limits administration
// (ORG-USAGE-2026 roles UX revamp)
//
// Where a super admin (or a delegate) decides how much each role may use, over
// whatever period they choose, and who else is allowed to change these rules.
//
// Design intent: everything is stated in plain language. A limit is never just
// a number in a box — the editor says what it means, when it resets, and who
// it affects, and the panel is explicit in the one case where limits are
// configured but not enforced (the workspace billing kill switch). Limits are
// $-first (2026-08-03): dollars lead, token caps are the optional extra, and
// one-click presets apply common limits instantly. Limits bind in EVERY billed
// workspace, trial and comped included.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  getMemberLimitStatuses,
  getUsageLimits,
  setMemberUsageLimits,
  setRoleUsageLimits,
  type MemberLimitStatus,
  type MemberLimitSummary,
  type RoleLimitSummary,
  type UsageLimitWindow,
  type UsageLimitWindowKind,
} from '../../services/rbacService';
import { useRbac } from '../../hooks/useRbac';
import { useTeamLiveRevision } from '../../hooks/useTeamLiveRevision';
import {
  USAGE_MIN_REFETCH_INTERVAL_MS,
  subscribableKinds,
} from '../../services/teamLive/teamLivePolicy';
import ConfirmDialog from './ConfirmDialog';

// ── styles ───────────────────────────────────────────────────────────────────

const Intro = styled.p`
  color: #666;
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0 0 1.25rem;
`;

const NoticeBanner = styled.div`
  border: 1px solid #ece4d3;
  background: #faf8f3;
  color: #6b5d3f;
  border-radius: 8px;
  padding: 0.75rem 0.875rem;
  font-size: 0.8125rem;
  line-height: 1.55;
  margin-bottom: 1.25rem;
`;

const SubTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const SubTab = styled.button<{ $active: boolean }>`
  background: ${(p) => (p.$active ? 'var(--brand-gold)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--brand-charcoal)' : '#666')};
  border: 1px solid ${(p) => (p.$active ? 'var(--brand-gold)' : '#e0e0e0')};
  border-radius: 6px;
  padding: 0.375rem 0.875rem;
  font-size: 0.8125rem;
  cursor: pointer;
  text-transform: lowercase;
`;

const Card = styled.div`
  border: 1px solid var(--brand-line);
  border-radius: 10px;
  padding: 1rem 1.125rem;
  margin-bottom: 0.75rem;
  background: #fff;
`;

const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Name = styled.div`
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--brand-black);
`;

const Sub = styled.div`
  font-size: 0.75rem;
  color: var(--brand-text-muted);
  margin-top: 0.1875rem;
`;

const LimitSummary = styled.ul`
  margin: 0.75rem 0 0;
  padding: 0;
  list-style: none;

  li {
    font-size: 0.8125rem;
    color: #444;
    padding: 0.25rem 0 0.25rem 1rem;
    position: relative;
    line-height: 1.5;
  }

  li::before {
    content: '•';
    position: absolute;
    left: 0;
    color: var(--brand-gold);
  }
`;

const Unlimited = styled.div`
  font-size: 0.8125rem;
  color: var(--brand-text-muted);
  margin-top: 0.625rem;
`;

const SmallBtn = styled.button`
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.3125rem 0.75rem;
  font-size: 0.8125rem;
  color: #666;
  cursor: pointer;
  text-transform: lowercase;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const PrimaryBtn = styled(SmallBtn)`
  background: var(--brand-gold);
  border-color: var(--brand-gold);
  color: var(--brand-charcoal);

  &:hover:not(:disabled) {
    opacity: 0.9;
    background: var(--brand-gold);
  }
`;

/* One-click preset chips — applying a common limit is a single click. */
const PresetRow = styled.div`
  display: flex;
  gap: 0.375rem;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 0.625rem;
`;

const PresetLabel = styled.span`
  font-size: 0.6875rem;
  color: var(--brand-text-muted);
  text-transform: lowercase;
`;

const PresetChip = styled.button`
  background: #faf8f3;
  border: 1px solid #ece4d3;
  border-radius: 999px;
  padding: 0.1875rem 0.625rem;
  font-size: 0.75rem;
  color: #6b5d3f;
  cursor: pointer;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  &:hover:not(:disabled) {
    background: #f2edda;
    border-color: var(--brand-gold);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const EditorRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
  padding: 0.625rem 0;
  border-bottom: 1px solid #f0f0ee;

  &:last-of-type {
    border-bottom: none;
  }
`;

const Input = styled.input`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.375rem 0.5rem;
  font-size: 0.8125rem;
  width: 120px;
  font-variant-numeric: tabular-nums;
`;

const Select = styled.select`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.375rem 0.5rem;
  font-size: 0.8125rem;
  background: #fff;
  cursor: pointer;
`;

const RowNote = styled.span`
  font-size: 0.75rem;
  color: var(--brand-text-muted);
  flex: 1 1 100%;
`;

const EditorActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.875rem;
`;

// ── "usage now" overview styles ─────────────────────────────────────────────

const MeterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.8125rem;
  color: #555;
  margin-top: 0.375rem;
`;

const MeterTrack = styled.div`
  flex: 1;
  min-width: 120px;
  height: 6px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.07);
  overflow: hidden;
`;

const MeterFill = styled.div<{ $pct: number; $level: string }>`
  width: ${(p) => Math.min(100, Math.max(2, p.$pct))}%;
  height: 100%;
  border-radius: 3px;
  background: ${(p) =>
    p.$level === 'reached' ? '#b91c1c' : p.$level === 'critical' ? '#c2660b' : p.$level === 'approaching' ? '#b8860b' : '#4a7c59'};
`;

const MeterPct = styled.span<{ $level: string }>`
  min-width: 3.5rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$level === 'reached' ? '#b91c1c' : p.$level === 'critical' ? '#c2660b' : 'inherit')};
  font-weight: ${(p) => (p.$level === 'none' ? 400 : 600)};
`;

const StateNote = styled.div`
  padding: 2.5rem 0;
  text-align: center;
  color: var(--brand-text-muted);
  font-size: 0.875rem;
`;

const ErrorState = styled.div`
  padding: 0.75rem 1rem;
  border: 1px solid rgba(153, 27, 27, 0.25);
  background: rgba(153, 27, 27, 0.05);
  color: #991b1b;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

// ── plain-language helpers (mirrors functions/src/lib/limitWindows.ts) ────────

function formatTokenCount(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m >= 10 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (n >= 1_000) {
    const k = n / 1_000;
    return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '')}K`;
  }
  return String(n);
}

function formatUsdAmount(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}

function hasCap(v: number | undefined | null): v is number {
  return v !== undefined && v !== null && Number.isFinite(Number(v));
}

export function describeLimitWindow(w: UsageLimitWindow): string {
  const parts: string[] = [];
  if (hasCap(w.costUsdLimit)) parts.push(formatUsdAmount(w.costUsdLimit));
  if (hasCap(w.tokenLimit)) parts.push(`${formatTokenCount(w.tokenLimit)} tokens`);
  // "or" is honest: hitting EITHER cap pauses usage until the window resets.
  const amounts = parts.join(' or ') || 'no cap';
  switch (w.kind) {
    case 'calendar-day':
      return `${amounts} per calendar day — resets daily at 00:00 UTC`;
    case 'calendar-week':
      return `${amounts} per calendar week — resets Mondays at 00:00 UTC`;
    case 'calendar-month':
      return `${amounts} per calendar month — resets on the 1st at 00:00 UTC`;
    case 'rolling':
      return `${amounts} per rolling ${w.amount} ${w.unit} — always measured against the last ${w.amount} ${w.unit}`;
    default:
      return amounts;
  }
}

const WINDOW_OPTIONS: Array<{ value: UsageLimitWindowKind; label: string }> = [
  { value: 'calendar-day', label: 'per day' },
  { value: 'calendar-week', label: 'per week' },
  { value: 'calendar-month', label: 'per month' },
  { value: 'rolling', label: 'per rolling period' },
];

let windowSeq = 0;
let windowSeqPreset = 0;
function newWindow(): UsageLimitWindow {
  windowSeq += 1;
  return {
    id: `w_new_${Date.now()}_${windowSeq}`,
    kind: 'calendar-month',
    // $-first (owner directive 2026-08-03): dollars are what people reason in.
    costUsdLimit: 50,
  };
}

// One-click starting points (owner directive 2026-08-03): a single click on a
// role or member card applies the limit immediately — no editor, no typing.
// Clicking a preset REPLACES any existing window of the same shape.
const QUICK_PRESETS: Array<{ label: string; kind: UsageLimitWindowKind; costUsdLimit: number }> = [
  { label: '$10 / day', kind: 'calendar-day', costUsdLimit: 10 },
  { label: '$50 / week', kind: 'calendar-week', costUsdLimit: 50 },
  { label: '$200 / month', kind: 'calendar-month', costUsdLimit: 200 },
];

// ── window editor ────────────────────────────────────────────────────────────

const WindowEditor: React.FC<{
  windows: UsageLimitWindow[];
  onChange: (windows: UsageLimitWindow[]) => void;
}> = ({ windows, onChange }) => {
  const update = (idx: number, patch: Partial<UsageLimitWindow>) => {
    const next = windows.map((w, i) => (i === idx ? { ...w, ...patch } : w));
    onChange(next);
  };

  // Empty input = no cap of that type; a window needs at least one cap and the
  // server rejects a window with neither, so the row note flags it early.
  const parseCap = (raw: string): number | undefined => {
    const trimmed = raw.trim();
    if (trimmed === '') return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? Math.max(0, n) : undefined;
  };

  return (
    <div>
      {windows.map((w, idx) => (
        <EditorRow key={w.id}>
          {/* $-first (owner directive 2026-08-03): dollars lead, tokens are the
              optional extra cap. Either one alone is a complete limit. */}
          <span style={{ fontSize: '0.8125rem', color: '#666' }}>$</span>
          <Input
            type="number"
            min={0}
            step={5}
            style={{ width: '90px' }}
            value={w.costUsdLimit ?? ''}
            placeholder="no $ cap"
            onChange={(e) => update(idx, { costUsdLimit: parseCap(e.target.value) })}
            aria-label="dollar limit"
          />
          <Input
            type="number"
            min={0}
            step={1000}
            style={{ width: '110px' }}
            value={w.tokenLimit ?? ''}
            placeholder="no token cap"
            onChange={(e) => {
              const cap = parseCap(e.target.value);
              update(idx, { tokenLimit: cap === undefined ? undefined : Math.floor(cap) });
            }}
            aria-label="token limit"
          />
          <span style={{ fontSize: '0.8125rem', color: '#666' }}>tokens</span>
          <Select
            value={w.kind}
            onChange={(e) => {
              const kind = e.target.value as UsageLimitWindowKind;
              update(idx, {
                kind,
                ...(kind === 'rolling'
                  ? { amount: w.amount ?? 24, unit: w.unit ?? 'hours' }
                  : { amount: undefined, unit: undefined }),
              });
            }}
            aria-label="limit period"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>

          {w.kind === 'rolling' && (
            <>
              <Input
                type="number"
                min={1}
                style={{ width: '70px' }}
                value={w.amount ?? 24}
                onChange={(e) => update(idx, { amount: Math.max(1, Number(e.target.value) || 1) })}
                aria-label="rolling amount"
              />
              <Select
                value={w.unit ?? 'hours'}
                onChange={(e) => update(idx, { unit: e.target.value as 'hours' | 'days' })}
                aria-label="rolling unit"
              >
                <option value="hours">hours</option>
                <option value="days">days</option>
              </Select>
            </>
          )}

          <SmallBtn onClick={() => onChange(windows.filter((_, i) => i !== idx))}>
            remove
          </SmallBtn>

          <RowNote>
            {hasCap(w.costUsdLimit) || hasCap(w.tokenLimit)
              ? describeLimitWindow(w)
              : 'set a dollar amount or a token cap for this limit.'}
          </RowNote>
        </EditorRow>
      ))}

      <EditorActions>
        <SmallBtn onClick={() => onChange([...windows, newWindow()])}>+ add a limit</SmallBtn>
      </EditorActions>
    </div>
  );
};

// ── panel ────────────────────────────────────────────────────────────────────

interface Props {
  canManage: boolean;
  canDelegate: boolean;
}

const UsageLimitsPanel: React.FC<Props> = ({ canManage, canDelegate }) => {
  const { hasPermission } = useRbac();
  const [roles, setRoles] = useState<RoleLimitSummary[]>([]);
  const [members, setMembers] = useState<MemberLimitSummary[]>([]);
  const [enforcement, setEnforcement] = useState<{ enforced: boolean; reason: string | null }>({
    enforced: true,
    reason: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'roles' | 'members' | 'status'>('roles');

  // "usage now" overview — loaded lazily the first time the tab opens.
  const [statuses, setStatuses] = useState<MemberLimitStatus[] | null>(null);
  const [statusTruncation, setStatusTruncation] = useState<{ truncated: boolean; total: number }>({ truncated: false, total: 0 });
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Last time the meters were actually fetched — the floor between two live
  // refreshes is enforced here because the signal behind them (account_usage)
  // moves on EVERY turn anyone in the workspace takes.
  const lastStatusFetchMsRef = useRef(0);

  const loadStatuses = useCallback(async (opts: { background?: boolean } = {}) => {
    const background = opts.background === true;
    if (!background) setStatusLoading(true);
    setStatusError(null);
    lastStatusFetchMsRef.current = Date.now();
    try {
      const res = await getMemberLimitStatuses();
      setStatuses(res.statuses ?? []);
      setStatusTruncation({ truncated: res.truncated === true, total: res.totalWithLimits ?? 0 });
    } catch (err: any) {
      // A failed background tick keeps the meters that are already drawn.
      if (!background) setStatusError(err?.message ?? 'could not load member usage');
    } finally {
      if (!background) setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'status' && statuses === null && !statusLoading) loadStatuses();
  }, [view, statuses, statusLoading, loadStatuses]);

  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [draft, setDraft] = useState<UsageLimitWindow[]>([]);
  const [saving, setSaving] = useState(false);
  const [applyingPreset, setApplyingPreset] = useState<string | null>(null);
  const [clearTarget, setClearTarget] = useState<{ kind: 'role' | 'member'; id: string; name: string } | null>(null);

  const [resolvedAccountId, setResolvedAccountId] = useState<string | null>(null);

  const load = useCallback(async (opts: { background?: boolean } = {}) => {
    const background = opts.background === true;
    if (!background) setLoading(true);
    setError(null);
    try {
      const res = await getUsageLimits();
      setRoles(res.roles ?? []);
      setMembers(res.members ?? []);
      setEnforcement(res.enforcement ?? { enforced: true, reason: null });
      setResolvedAccountId((res as any).resolvedAccountId ?? null);
    } catch (err: any) {
      if (!background) setError(err?.message ?? 'could not load usage limits');
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Live updates (2026-09-17) ──────────────────────────────────────────────
  // Two independent signals, because this panel has two independent loaders.
  //
  // CONFIG (who is capped at what) rides the roles + members documents: a role
  // limit is stored on the role doc and denormalized onto every member doc by
  // recomputeAccountRbac, and a member override is stored on the member doc.
  const configKinds = useMemo(
    () =>
      subscribableKinds(['roles', 'members'], {
        hasTeamView: hasPermission('team.view'),
        hasRolesView: hasPermission('roles.view'),
      }),
    [hasPermission],
  );
  const configRevision = useTeamLiveRevision({ accountId: resolvedAccountId, kinds: configKinds });
  useEffect(() => {
    if (configRevision > 0) void load({ background: true });
  }, [configRevision, load]);

  // METERS ("usage now") ride accounts/{id}/account_usage/{month}, which is
  // written inside the SAME transaction as every per-member usage rollup — one
  // document, readable by any active member, carrying account-level totals only
  // (member_usage itself is owner-only for a query, and would disclose every
  // person's spend). Subscribed ONLY while the meters are actually on screen.
  const usageRevision = useTeamLiveRevision({
    accountId: resolvedAccountId,
    kinds: ['accountUsage'],
    enabled: view === 'status',
  });
  useEffect(() => {
    if (usageRevision === 0 || view !== 'status') return;
    const sinceMs = Date.now() - lastStatusFetchMsRef.current;
    if (sinceMs >= USAGE_MIN_REFETCH_INTERVAL_MS) {
      void loadStatuses({ background: true });
      return;
    }
    // Inside the floor: take the refresh at the end of it rather than dropping
    // it, so the last change in a busy stretch is never the one that is lost.
    const timer = setTimeout(
      () => { void loadStatuses({ background: true }); },
      USAGE_MIN_REFETCH_INTERVAL_MS - sinceMs,
    );
    return () => clearTimeout(timer);
  }, [usageRevision, view, loadStatuses]);

  const roleNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of roles) map[r.roleId] = r.name;
    return map;
  }, [roles]);

  const startEditRole = (role: RoleLimitSummary) => {
    setEditingMemberId(null);
    setEditingRoleId(role.roleId);
    setDraft(role.usageLimits?.windows ? [...role.usageLimits.windows] : [newWindow()]);
  };

  const startEditMember = (member: MemberLimitSummary) => {
    setEditingRoleId(null);
    setEditingMemberId(member.userId);
    setDraft(
      member.usageLimitOverride?.windows ? [...member.usageLimitOverride.windows] : [newWindow()]
    );
  };

  // Every window in the draft needs at least one cap — catch it before the
  // round-trip so the admin gets an instant, specific message.
  const draftProblem = (): string | null => {
    const capless = draft.findIndex((w) => !hasCap(w.costUsdLimit) && !hasCap(w.tokenLimit));
    if (capless >= 0) return `limit ${capless + 1} needs a dollar amount or a token cap.`;
    return null;
  };

  const saveRole = async (roleId: string) => {
    const problem = draftProblem();
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError(null);
    try {
      await setRoleUsageLimits(roleId, draft.length > 0 ? { windows: draft } : null);
      setEditingRoleId(null);
      await load({ background: true });
    } catch (err: any) {
      setError(err?.message ?? 'could not save the limit');
    } finally {
      setSaving(false);
    }
  };

  const saveMember = async (userId: string) => {
    const problem = draftProblem();
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError(null);
    try {
      await setMemberUsageLimits(userId, draft.length > 0 ? { windows: draft } : null);
      setEditingMemberId(null);
      await load({ background: true });
    } catch (err: any) {
      setError(err?.message ?? 'could not save the override');
    } finally {
      setSaving(false);
    }
  };

  /**
   * One-click preset (owner directive 2026-08-03): applies immediately —
   * no editor, no confirm. Replaces an existing window of the same shape
   * (the server rejects duplicate shapes), keeps every other window.
   */
  const applyPreset = async (
    target: { kind: 'role'; id: string } | { kind: 'member'; id: string },
    existingWindows: UsageLimitWindow[] | undefined,
    preset: (typeof QUICK_PRESETS)[number]
  ) => {
    const chipKey = `${target.kind}:${target.id}:${preset.label}`;
    setApplyingPreset(chipKey);
    setError(null);
    try {
      windowSeqPreset += 1;
      const next = (existingWindows ?? []).filter((w) => w.kind !== preset.kind);
      next.push({
        id: `w_preset_${Date.now()}_${windowSeqPreset}`,
        kind: preset.kind,
        costUsdLimit: preset.costUsdLimit,
      });
      if (target.kind === 'role') await setRoleUsageLimits(target.id, { windows: next });
      else await setMemberUsageLimits(target.id, { windows: next });
      await load({ background: true });
    } catch (err: any) {
      setError(err?.message ?? 'could not apply the limit');
    } finally {
      setApplyingPreset(null);
    }
  };

  const clearLimits = async () => {
    if (!clearTarget) return;
    setSaving(true);
    setError(null);
    try {
      if (clearTarget.kind === 'role') await setRoleUsageLimits(clearTarget.id, null);
      else await setMemberUsageLimits(clearTarget.id, null);
      setClearTarget(null);
      await load({ background: true });
    } catch (err: any) {
      setError(err?.message ?? 'could not clear the limit');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <StateNote>loading usage limits…</StateNote>;

  return (
    <>
      <Intro>
        set how much members may spend — in dollars, or tokens if you prefer — over a period you
        choose. limits attach to roles, so everyone with that role shares the same allowance — with
        an optional per-person override when someone needs different terms. use the one-click
        presets for common limits, or open the editor for exact amounts. members always see their
        own usage and get a heads-up well before a limit is reached — nobody is ever stopped
        without warning. account owners are never limited.
        {canDelegate && (
          <>
            {' '}
            you can also decide who else may change these rules: grant “manage usage limits” on the
            roles tab, and “delegate usage limits” to let someone extend that power further.
          </>
        )}
      </Intro>

      {!enforcement.enforced && enforcement.reason && (
        <NoticeBanner>{enforcement.reason}</NoticeBanner>
      )}

      {error && <ErrorState>{error}</ErrorState>}

      <SubTabs>
        <SubTab $active={view === 'roles'} onClick={() => setView('roles')}>
          by role
        </SubTab>
        <SubTab $active={view === 'members'} onClick={() => setView('members')}>
          per-person overrides
        </SubTab>
        <SubTab $active={view === 'status'} onClick={() => setView('status')}>
          usage now
        </SubTab>
      </SubTabs>

      {view === 'status' && (
        <>
          {statusLoading && <StateNote>checking everyone's usage…</StateNote>}
          {!statusLoading && statusError && (
            <ErrorState>
              <span>{statusError}</span>
              <SmallBtn onClick={() => { void loadStatuses(); }}>retry</SmallBtn>
            </ErrorState>
          )}
          {!statusLoading && !statusError && statuses !== null && statuses.length === 0 && (
            <StateNote>
              nobody has a usage limit yet — set one on a role (or as a per-person override) and
              their live usage will appear here.
            </StateNote>
          )}
          {!statusLoading && !statusError && (statuses ?? []).map((s) => (
            <Card key={s.userId}>
              <CardHead>
                <div>
                  <Name>{s.displayName || s.email || s.userId.slice(0, 12)}</Name>
                  <Sub>{s.source === 'override' ? 'per-person override' : 'limits from roles'}</Sub>
                </div>
                <SmallBtn onClick={() => { void loadStatuses({ background: true }); }}>refresh</SmallBtn>
              </CardHead>
              {s.windows.map((w, i) => {
                const pct = Math.min(100, Math.round(w.ratio * 100));
                return (
                  <MeterRow key={`${s.userId}-${i}`}>
                    <span style={{ minWidth: '7.5rem' }}>{w.label}</span>
                    <MeterTrack>
                      <MeterFill $pct={pct} $level={w.level} />
                    </MeterTrack>
                    <MeterPct $level={w.level}>
                      {w.exceeded ? 'at limit' : `${Math.min(99, Math.floor(w.ratio * 100))}%`}
                    </MeterPct>
                    <span style={{ minWidth: '9rem', color: '#888' }}>
                      {w.metric === 'usd'
                        ? `${formatUsdAmount(w.used)} of ${formatUsdAmount(w.limit)}`
                        : `${formatTokenCount(w.used)} of ${formatTokenCount(w.limit)} tokens`}
                    </span>
                  </MeterRow>
                );
              })}
            </Card>
          ))}
          {!statusLoading && !statusError && statusTruncation.truncated && (
            <StateNote>
              showing the first {statuses?.length ?? 0} of {statusTruncation.total} members with
              limits — see the activity page for the full picture.
            </StateNote>
          )}
        </>
      )}

      {view === 'roles' &&
        roles
          .filter((r) => r.status !== 'disabled')
          .map((role) => (
            <Card key={role.roleId}>
              <CardHead>
                <div>
                  <Name>{role.name}</Name>
                  <Sub>
                    {role.assignedMemberCount} member
                    {role.assignedMemberCount === 1 ? '' : 's'}
                    {role.system ? ' · built-in role' : ''}
                  </Sub>
                </div>
                {canManage && editingRoleId !== role.roleId && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <SmallBtn onClick={() => startEditRole(role)}>
                      {role.usageLimits?.windows?.length ? 'edit limits' : 'set a limit'}
                    </SmallBtn>
                    {!!role.usageLimits?.windows?.length && (
                      <SmallBtn
                        onClick={() =>
                          setClearTarget({ kind: 'role', id: role.roleId, name: role.name })
                        }
                      >
                        remove limits
                      </SmallBtn>
                    )}
                  </div>
                )}
              </CardHead>

              {editingRoleId === role.roleId ? (
                <>
                  <WindowEditor windows={draft} onChange={setDraft} />
                  <EditorActions>
                    <PrimaryBtn disabled={saving} onClick={() => saveRole(role.roleId)}>
                      {saving ? 'saving…' : 'save limits'}
                    </PrimaryBtn>
                    <SmallBtn disabled={saving} onClick={() => setEditingRoleId(null)}>
                      cancel
                    </SmallBtn>
                  </EditorActions>
                </>
              ) : (
                <>
                  {role.usageLimits?.windows?.length ? (
                    <LimitSummary>
                      {role.usageLimits.windows.map((w) => (
                        <li key={w.id}>{describeLimitWindow(w)}</li>
                      ))}
                    </LimitSummary>
                  ) : (
                    <Unlimited>
                      no limit — members with this role can use as much as they need.
                    </Unlimited>
                  )}
                  {canManage && (
                    <PresetRow>
                      <PresetLabel>quick set:</PresetLabel>
                      {QUICK_PRESETS.map((preset) => {
                        const chipKey = `role:${role.roleId}:${preset.label}`;
                        return (
                          <PresetChip
                            key={preset.label}
                            disabled={applyingPreset !== null}
                            onClick={() =>
                              applyPreset(
                                { kind: 'role', id: role.roleId },
                                role.usageLimits?.windows,
                                preset
                              )
                            }
                          >
                            {applyingPreset === chipKey ? 'applying…' : `+ ${preset.label}`}
                          </PresetChip>
                        );
                      })}
                    </PresetRow>
                  )}
                </>
              )}
            </Card>
          ))}

      {view === 'members' &&
        members
          .filter((m) => !m.isOwner && m.status !== 'removed')
          .map((member) => (
            <Card key={member.userId}>
              <CardHead>
                <div>
                  <Name>{member.displayName || member.email || member.userId.slice(0, 12)}</Name>
                  <Sub>
                    {(member.assignedRoleIds ?? [])
                      .map((rid) => roleNames[rid] ?? rid)
                      .join(', ') || 'no roles'}
                  </Sub>
                </div>
                {canManage && editingMemberId !== member.userId && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <SmallBtn onClick={() => startEditMember(member)}>
                      {member.usageLimitOverride?.windows?.length
                        ? 'edit override'
                        : 'set an override'}
                    </SmallBtn>
                    {!!member.usageLimitOverride?.windows?.length && (
                      <SmallBtn
                        onClick={() =>
                          setClearTarget({
                            kind: 'member',
                            id: member.userId,
                            name: member.displayName || member.email || member.userId,
                          })
                        }
                      >
                        remove override
                      </SmallBtn>
                    )}
                  </div>
                )}
              </CardHead>

              {editingMemberId === member.userId ? (
                <>
                  <WindowEditor windows={draft} onChange={setDraft} />
                  <EditorActions>
                    <PrimaryBtn disabled={saving} onClick={() => saveMember(member.userId)}>
                      {saving ? 'saving…' : 'save override'}
                    </PrimaryBtn>
                    <SmallBtn disabled={saving} onClick={() => setEditingMemberId(null)}>
                      cancel
                    </SmallBtn>
                  </EditorActions>
                </>
              ) : (
                <>
                  {member.usageLimitOverride?.windows?.length ? (
                    <LimitSummary>
                      {member.usageLimitOverride.windows.map((w) => (
                        <li key={w.id}>
                          {describeLimitWindow(w)} <em style={{ color: '#999' }}>(override)</em>
                        </li>
                      ))}
                    </LimitSummary>
                  ) : member.effectiveUsageLimits?.windows?.length ? (
                    <LimitSummary>
                      {member.effectiveUsageLimits.windows.map((w) => (
                        <li key={w.id}>
                          {describeLimitWindow(w)} <em style={{ color: '#999' }}>(from roles)</em>
                        </li>
                      ))}
                    </LimitSummary>
                  ) : (
                    <Unlimited>no limit.</Unlimited>
                  )}
                  {canManage && (
                    <PresetRow>
                      <PresetLabel>quick override:</PresetLabel>
                      {QUICK_PRESETS.map((preset) => {
                        const chipKey = `member:${member.userId}:${preset.label}`;
                        return (
                          <PresetChip
                            key={preset.label}
                            disabled={applyingPreset !== null}
                            onClick={() =>
                              applyPreset(
                                { kind: 'member', id: member.userId },
                                member.usageLimitOverride?.windows,
                                preset
                              )
                            }
                          >
                            {applyingPreset === chipKey ? 'applying…' : `+ ${preset.label}`}
                          </PresetChip>
                        );
                      })}
                    </PresetRow>
                  )}
                </>
              )}
            </Card>
          ))}

      <ConfirmDialog
        isOpen={!!clearTarget}
        error={clearTarget ? error : null}
        title="remove usage limits"
        busy={saving}
        confirmLabel="remove limits"
        message={`Remove the usage ${clearTarget?.kind === 'role' ? 'limits' : 'override'} for ${clearTarget?.name ?? ''}?`}
        consequence={
          clearTarget?.kind === 'role'
            ? 'Members with this role go back to unlimited usage, unless another role or a personal override still limits them.'
            : 'This person goes back to the limits that come from their roles.'
        }
        onConfirm={clearLimits}
        onCancel={() => setClearTarget(null)}
      />
    </>
  );
};

export default UsageLimitsPanel;
